import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { observer } from "mobx-react-lite";
import { useServerTable } from "@/hooks/useServerTable";
import { getIAndIv1Columns } from "./Columns";
import { FC, useMemo, useRef } from "react";
import { DEFAULT_I_AND_I_TIME_PERIOD, IAndITimePeriodsValue } from "@/config/date";
import { CUSTOM_FILTERS } from "@/config/list";
import { TimePeriodFilter } from "./TimePeriodFilter";
import { getTableStructureFilterParams } from "@/utils/filters";
import { StructureFilters } from "@/components/filters/StructureFilters";
import { ListMapView } from "@/components/ListMapView";
import type { ExtraView } from "@/components/ListMapView";
import { useMapStore } from "@/hooks/useMapStore";
import type { MarkerDataConfig, MarkerLayerConfig } from "@/components/Map/stores/MapStore";
import { DataDrivenPropertyValueSpecification } from "mapbox-gl";
import { SearchInput } from "@/components/SearchInput";
import { PopUp } from "./PopUp";
import { MostDownstreamFilter } from "./MostDownstreamFilter";
import { HasDataFilter } from "./HasDataFilter";
import { ExpandedIAndIMembers } from "./ExpandedIAndIMembers";
import { ExpandColumn } from "@/components/tableColumns/ExpandColumn";
import { IAndIFlow } from "@/components/IAndI/IAndIFlow";
import { WorkflowIcon } from "lucide-react";
import { infiltrationAndInflow } from "@/endpoints/dryp/infiltrationAndInflow";
import { getColumnUnits, useIAndIListColumnMaxes, useRefetchColumnMaxes } from "./useIAndIListColumnMaxes";

export const getColorForIAndIMarker = (pipeLength?: number, stats?: { min: number; max: number }): string => {
	const GRAY = "#9CA3AF";
	if (!stats || pipeLength == null || !Number.isFinite(pipeLength)) return GRAY;

	const { max } = stats;
	if (!Number.isFinite(max) || max <= 0) return GRAY;

	const v = Math.min(max, Math.max(0, pipeLength));
	const t = v / max; // normalize 0 → 1

	// RGB gradient: green (#00FF00) → red (#FF0000)
	const r = Math.round(255 * t);
	const g = Math.round(255 * (1 - t));
	const toHex = (n: number) => n.toString(16).padStart(2, "0");

	return `#${toHex(r)}${toHex(g)}00`;
};

const iAndICircleColor = [
	"coalesce",
	["get", "markerColor"],
	"#9CA3AF",
] as unknown as DataDrivenPropertyValueSpecification<string>;

export const iAndILayerConfig: MarkerLayerConfig = {
	paint: {
		"circle-radius": 6,
		"circle-color": iAndICircleColor,
		"circle-stroke-color": "#ffffff",
		"circle-stroke-width": 1,
	},
};

interface IAndIListProps {
	enableUrl?: boolean;
	enableFlow?: boolean;
}

export const iAndIDataConfig: MarkerDataConfig<IAndIMapItem> = {
	getCoordinates: item => [item.structure.coordinate.longitude, item.structure.coordinate.latitude],
	getId: item => String(item.structure.id),
	getProperties: item => ({
		markerColor: item.markerColor,
	}),
};

export type IAndIMapItem = Dryp.IAndISummary & { markerColor: string };

export const IAndIList: FC<IAndIListProps> = observer(({ enableUrl = false, enableFlow = false }) => {
	const iAndIListApi = useAsyncStateStore(infiltrationAndInflow.getSummaries, { debounce: 300 });
	const mapIAndIApi = useAsyncStateStore(infiltrationAndInflow.getSummaries, { debounce: 300 });
	const { apis: columnApis, fetchMaxes: fetchColumnMaxes } = useIAndIListColumnMaxes();

	const { volume: volumeUnit, flowPerLength: flowPerLengthUnit } = getColumnUnits(iAndIListApi.data?.items ?? []);

	const refreshRef = useRef<(() => void) | undefined>(undefined);

	const columns = useMemo(
		() => [
			ExpandColumn<Dryp.IAndISummary>(),
			...getIAndIv1Columns(columnApis, { volume: volumeUnit, flowPerLength: flowPerLengthUnit }, () =>
				refreshRef.current?.(),
			),
		],
		[columnApis, volumeUnit, flowPerLengthUnit],
	);

	const { table, refresh, tableState } = useServerTable({
		columns,
		data: iAndIListApi.data?.items || [],
		fetchFn: params =>
			iAndIListApi.fetch(
				{
					pageNumber: params.page,
					pageSize: params.pageSize,
				},
				params.filters?.find(f => f.id === CUSTOM_FILTERS.timePeriod)?.value,
				params.sort,
				getTableStructureFilterParams(params?.filters),
			),
		initialValues: {
			page: 0,
			pageSize: 10,
			filters: [
				{
					id: CUSTOM_FILTERS.timePeriod,
					value: [DEFAULT_I_AND_I_TIME_PERIOD],
				},
			],
			sorting: [
				{
					id: "infiltrationVolume",
					desc: true,
				},
			],
			columnVisibility: {
				structureId: false,
				createdAt: false,
				"structure.networkType": false,
			},
		},
		enableUrlParams: enableUrl,
		initFromUrl: enableUrl,
	});

	refreshRef.current = refresh;

	const mapStore = useMapStore(iAndILayerConfig, iAndIDataConfig);

	mapStore.onFetch = async () => {
		const filters = mapStore.filters.getFilters();
		const timePeriod = filters.find(f => f.id === CUSTOM_FILTERS.timePeriod)?.value as IAndITimePeriodsValue;
		const structureFilters = getTableStructureFilterParams(filters);

		await mapIAndIApi.fetch(
			{
				pageNumber: 0,
				pageSize: 100000,
			},
			timePeriod,
			undefined,
			structureFilters,
		);

		if (!mapStore.isLoaded) return;
		const mapItems = mapIAndIApi.data?.items || [];
		const maxCombinedFlowPerLength = mapItems.reduce(
			(currentMax, mapItem) => Math.max(currentMax, mapItem.total?.flowPerLength?.value ?? 0),
			0,
		);
		const stats = maxCombinedFlowPerLength > 0 ? { min: 0, max: maxCombinedFlowPerLength } : undefined;
		const items = mapItems.map<IAndIMapItem>(item => {
			const value = item.total?.flowPerLength?.value;
			const markerColor = getColorForIAndIMarker(value, stats);

			return {
				...item,
				markerColor,
			};
		});

		mapStore.setMarkers(items);
		mapStore.fitToData(items, i => [i.structure.coordinate.longitude, i.structure.coordinate.latitude]);
	};

	const columnFilters = tableState.filtersSnapshot;

	const currentTimePeriod =
		(
			columnFilters?.find(filter => filter.id === CUSTOM_FILTERS.timePeriod)?.value as
				| IAndITimePeriodsValue[]
				| undefined
		)?.[0] ?? DEFAULT_I_AND_I_TIME_PERIOD;

	// Bar maxes depend only on the filters (not on page/sort), so refetch on filter changes.
	const listStructureFilters = getTableStructureFilterParams(columnFilters);
	useRefetchColumnMaxes(fetchColumnMaxes, currentTimePeriod, listStructureFilters);

	const extraViews: ExtraView<"flow">[] = enableFlow
		? [
				{
					key: "flow",
					label: "Flow view",
					icon: <WorkflowIcon className="h-4 w-4" />,
					render: () => <IAndIFlow columnFilters={columnFilters} />,
				},
			]
		: [];

	return (
		<ListMapView<Dryp.IAndISummary, "flow">
			extraViews={extraViews}
			filters={(filter, _view) => (
				<div className="flex flex-wrap md:flex-nowrap gap-2 items-end">
					<SearchInput
						value={(filter.getFilter(CUSTOM_FILTERS.search) as string | undefined) ?? ""}
						onChange={value => filter.setFilter(CUSTOM_FILTERS.search, value)}
					/>
					<TimePeriodFilter filter={filter} />
					<StructureFilters filter={filter} hideTypes />
					<MostDownstreamFilter filter={filter} />
					<HasDataFilter filter={filter} />
				</div>
			)}
			list={{
				table,
				tableName: "I and I",
				expandedRowPanel: row => (
					<ExpandedIAndIMembers
						downstreamStructureId={row.original.structure.id}
						downstreamStructureName={row.original.structure.name}
						timePeriod={currentTimePeriod}
						columnApis={columnApis}
					/>
				),
				toolbar: {
					refreshFunction: refresh,
					actionCoppyUrl: enableUrl,
				},
				pagination: {
					total: iAndIListApi.data?.query?.pagination.totalItems || 0,
				},
				layout: {
					loading: iAndIListApi.loading,
					errorMessage: iAndIListApi?.error?.message,
					emptyMessage: "No infiltration or inflow data found",
					spinnerText: [],
				},
			}}
			enableUrlParams={enableUrl}
			initFromUrl={enableUrl}
			map={{
				//@ts-ignore
				mapStore,
				loader: {
					isLoading: mapIAndIApi.loading,
				},
				initFromUrl: enableUrl,
				enableUrlParams: enableUrl,
				renderPopup: iAndI => <PopUp iAndI={iAndI} />,
			}}
		/>
	);
});
